import { motion } from 'framer-motion';
import { FileText, Calculator, Calendar, Banknote } from 'lucide-react';

const steps = [
  {
    icon: FileText,
    number: '01',
    title: 'Fahrzeugdaten senden',
    description: 'Füllen Sie unser einfaches Formular aus oder kontaktieren Sie uns direkt per WhatsApp.',
  },
  {
    icon: Calculator,
    number: '02',
    title: 'Bewertung erhalten',
    description: 'Wir bewerten Ihr Fahrzeug schnell und fair – Sie erhalten zeitnah ein unverbindliches Angebot.',
  },
  {
    icon: Calendar,
    number: '03',
    title: 'Termin abstimmen',
    description: 'Passt das Angebot? Dann vereinbaren wir einen Termin – bei Ihnen vor Ort oder bei uns.',
  },
  {
    icon: Banknote,
    number: '04',
    title: 'Auto verkaufen',
    description: 'Unkomplizierte Abwicklung, sofortige Bezahlung und auf Wunsch Abholung durch uns.',
  },
];

export const Process = () => {
  return (
    <section id="prozess" data-testid="process-section" className="py-20 md:py-28 bg-tiktok-black relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-tiktok-cyan/5 to-tiktok-pink/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-20"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-tiktok-cyan mb-4 block">
            So funktioniert's
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            In 4 einfachen Schritten zum Verkauf
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Schnell, transparent und unkompliziert – so einfach verkaufen Sie Ihr Auto bei uns.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line (Desktop) */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
              >
                {/* Step Card */}
                <div className="text-center">
                  {/* Icon Container */}
                  <div className="relative inline-block mb-6">
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto ${
                      index % 2 === 0 
                        ? 'bg-gradient-to-br from-tiktok-cyan/20 to-tiktok-cyan/5 border border-tiktok-cyan/30' 
                        : 'bg-gradient-to-br from-tiktok-pink/20 to-tiktok-pink/5 border border-tiktok-pink/30'
                    }`}>
                      <step.icon className={`w-8 h-8 ${index % 2 === 0 ? 'text-tiktok-cyan' : 'text-tiktok-pink'}`} />
                    </div>
                    {/* Step Number */}
                    <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                      index % 2 === 0 
                        ? 'bg-tiktok-cyan text-black' 
                        : 'bg-tiktok-pink text-white'
                    }`}>
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow (between steps on desktop) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-24 -right-4 transform translate-x-1/2">
                    <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-12 md:mt-16"
        >
          <button
            onClick={() => {
              const element = document.querySelector('#autoformular');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="process-cta"
            className="btn-gradient text-white font-bold px-8 py-4 rounded-xl hover:scale-105 transition-transform active:scale-95"
          >
            Jetzt Fahrzeug bewerten lassen
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
